import { ISystem } from "./ISystem";
import { App, createApp } from 'vue';
import { IEventBus } from "./IEventBus";
import { SystemStatus } from "./SystemStatus";
import { framework } from '@truckdown/jsclient/apis';
import { core } from '@truckdown/jsclient/icons';

import clickOutsideDirective from './VueClickOutsideDirective';
import { TinyEmitterEventBus } from './TinyEmitterEventBus';

const rootComponent: string = "rootComponent";
const defaultAppComponent: string = "#app";

export class VueSystem implements ISystem<App<Element>> {
    constructor() {
        this.internal_app = this.createApplication(true);

        this.status = SystemStatus.New;
        this.framework = framework.default;

        this.events = new TinyEmitterEventBus();
        this.settings = new Map<string, any>();
        this.fontLibrary = core.library;

        this.framework.registerService<IEventBus>('IEventBus', this.events);

        var data = this.getAntigorgeryData();
        this.antiForgeryHeader = data.header;
        this.antiForgeryToken = data.token;
    }

    private createApplication(initial?: boolean): App<Element> {
        var app = createApp({});
        app.directive('click-outside', clickOutsideDirective);
        app.config.errorHandler = (err) => {
            console.error(err);
        }

        if (!initial) {
            for (var i = 0; i < this.configures.length; i++) {
                this.configures[i](this.app);
            }
        }

        return app;
    }

    createApp(): App<Element> {
        return this.createApplication(false);
    }

    private mountApplication() {
        for (var i = 0; i < this.configures.length; i++) {
            this.configures[i](this.app);
        }
        console.log('Initializing Vue System.');
        this.app.mount(this.getSetting<string>(rootComponent, defaultAppComponent));
    }

    private internal_app: App<Element>;

    readonly events: IEventBus;
    status: SystemStatus;
    readonly framework: framework.IFramework;
    get app(): App<Element>
    {
        return this.internal_app;
    }
    readonly fontLibrary: core.Library;

    private readonly settings: Map<string, any>;

    getSetting<T>(name: string): T | null
    getSetting<T>(name: string, defaultValue: T): T
    getSetting<T>(name: string, defaultValue?: T): T | null {
        if (this.settings.has(name)) {
            return this.settings.get(name) as T;
        }
        if (defaultValue) {
            return defaultValue;
        } else {
            return null;
        }
    }

    setSetting<T>(name: string, value: T): void {
        this.settings.set(name, value);
    }

    private configures: ((app: App<Element>) => void)[] = [];

    configure(fn: (app: App<Element>) => void): void {
        this.configures.push(fn);
    }

    private getAntigorgeryData(): { 'field': string, 'header': string, 'token': string } {
        var meta = this.getMeta('antiforgery');
        if (meta != null) {
            var json = atob(meta);
            return JSON.parse(json) as { 'field': string, 'header': string, 'token': string };
        }

        return {
            'field': '',
            'header': '',
            'token': ''
        }

    }

    private getMeta(metaName: string): string | null {
        const metas = document.getElementsByTagName('meta');
        for (let i = 0; i < metas.length; i++) {
            if (metas[i].getAttribute('name') === metaName) {
                return metas[i].getAttribute('content') || null;
            }
        }
        return null;
    }

    readonly antiForgeryToken: string;
    readonly antiForgeryHeader: string;

    applyAntiForgeryToken(request: framework.IWebServiceRequest): void {
        request.headers[this.antiForgeryHeader] = this.antiForgeryToken;
    }

    initialize(): void {
        if (this.status == SystemStatus.New) {
            this.mountApplication();
            this.status = SystemStatus.Ready;
        } else {
            console.log('Error - system already initialized');
        }
    }
}
